import './ReportBar.scss';

import { Button, IconButton, Modal, Radio, RadioGroup, TableStatusChip, Typography } from '@trackman/web-shared-components';
import { Suspense, lazy, useState } from 'react';

import { AppPermissions } from 'constants/AppPermissions';
import { DefaultRoutes } from 'Routes';
import { ReactComponent as RightIcon } from 'svg/ArrowRight.svg';
import { useNavigate } from 'react-router';
import { useReportId } from 'hooks/helperHooks';
import { useTranslation } from 'react-i18next';
import { useApplicationUserRightsStore } from 'stores/ApplicationUserRightsStore';
import Alert from 'components/Alert/Alert';

const PrintReport = lazy(() => import('components/PrintReport/PrintReport'));
const DownloadReport = lazy(() => import('components/DownloadReport/DownloadReport'));

type Props = {
  reportName: string;
  isEditMode: boolean;
  setIsEditMode: (value: boolean) => void;
  saveLayoutForSession: () => void;
  saveLayoutForOrg: () => void;
  downloadName: string;
  hasUnsavedEdits: boolean;
  discardLayoutChanges: () => void;
};

type SaveMethod = 'session' | 'organization';

const ReportBar = ({
  reportName,
  isEditMode,
  setIsEditMode,
  saveLayoutForSession,
  saveLayoutForOrg,
  downloadName,
  hasUnsavedEdits,
  discardLayoutChanges,
}: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isCreate } = useReportId();
  const { IsAllowed } = useApplicationUserRightsStore();
  const [isSaveForOrgModalOpen, setIsSaveForOrgModalOpen] = useState(false);
  const [isDiscardChangesModalOpen, setIsDiscardChangesModalOpen] = useState(false);
  const [saveMethod, setSaveMethod] = useState<SaveMethod>(isCreate ? 'organization' : 'session');

  const canEditDefaultLayout = IsAllowed([AppPermissions.DefaultLayoutEdit]);

  function handleSave(): void {
    if (canEditDefaultLayout) {
      setSaveMethod(isCreate ? 'organization' : 'session');
      setIsSaveForOrgModalOpen(true);
    } else {
      saveLayoutForSession();
      setIsEditMode(false);
    }
  }

  function handleConfirmSaveFromOrgModal(): void {
    if (saveMethod === 'session') {
      saveLayoutForSession();
    } else {
      saveLayoutForOrg();
    }
    setIsSaveForOrgModalOpen(false);
    setIsEditMode(false);
  }

  function handleCancel(): void {
    if (hasUnsavedEdits) {
      setIsDiscardChangesModalOpen(true);
    } else {
      setIsEditMode(false);
    }
  }

  function handleDiscardChanges(): void {
    setIsDiscardChangesModalOpen(false);
    setIsEditMode(false);
    discardLayoutChanges();
  }

  return (
    <div className='ReportBar-container'>
      <div className={`ReportBar-title ${isEditMode ? 'is-editing' : ''}`}>
        <IconButton
          onClick={() => navigate(DefaultRoutes.Homepage, { viewTransition: true })}
          icon={() => <RightIcon style={{ transform: 'rotate(180deg)' }} />}
          className='ReportBar-icon'
        />
        <Typography variant='h2' component='div'>
          {reportName}
        </Typography>
        <TableStatusChip intent='info' label='Edit mode' className='ReportBar-editing-chip' />
      </div>
      <div className='ReportBar-buttons'>
        {isEditMode ? (
          <>
            <Button key='cancel' label={t('Cancel')} intent='default' onClick={handleCancel} />
            <Button label={t('Save')} intent='primary' onClick={handleSave} disabled={!hasUnsavedEdits} />
          </>
        ) : (
          <>
            {!isCreate && (
              <>
                <Suspense>
                  <PrintReport />
                </Suspense>
                <Suspense>
                  <DownloadReport downloadName={downloadName} />
                </Suspense>
              </>
            )}
            {(!isCreate || canEditDefaultLayout) && (
              <Button label={t('Edit layout')} intent='secondary' onClick={() => setIsEditMode(true)} className='ReportBar-edit-layout' />
            )}
          </>
        )}
      </div>
      <Modal
        title={t('save.modal.title')}
        open={isSaveForOrgModalOpen}
        onClose={() => setIsSaveForOrgModalOpen(false)}
        confirmBtn={{
          label: t('save.modal.confirmBtn'),
          onClick: handleConfirmSaveFromOrgModal,
        }}
        cancelBtn={{ label: t('Cancel'), onClick: () => setIsSaveForOrgModalOpen(false) }}
        style={{ zIndex: 2001 }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Typography component='div'>{t('save.modal.body')}</Typography>
          <RadioGroup
            style={{ gap: '16px' }}
            value={saveMethod}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSaveMethod((event.target as HTMLInputElement).value as SaveMethod)}
          >
            <Radio
              value={'session' satisfies SaveMethod}
              label={t('save.modal.sessionOptionLabel')}
              hint={t('save.modal.sessionOptionHint')}
              disabled={isCreate}
            />
            <Radio
              value={'organization' satisfies SaveMethod}
              label={t('save.modal.orgOptionLabel')}
              hint={t('save.modal.orgOptionHint')}
            />
          </RadioGroup>
          <Alert variant='warning' message={t('save.modal.warning')} />
        </div>
      </Modal>

      <Modal
        title={t('cancel.modal.title')}
        open={isDiscardChangesModalOpen}
        onClose={() => setIsDiscardChangesModalOpen(false)}
        confirmBtn={{
          label: t('cancel.modal.confirmBtn'),
          onClick: handleDiscardChanges,
        }}
        cancelBtn={{ label: t('cancel.modal.cancelBtn'), onClick: () => setIsDiscardChangesModalOpen(false) }}
        style={{ zIndex: 2001 }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Typography component='div'>{t('cancel.modal.body')}</Typography>
        </div>
      </Modal>
    </div>
  );
};

export default ReportBar;
